import { createSlice } from '@reduxjs/toolkit'
import { StoreStatus } from 'common/utils/types'
import {
  getAIResults,
  getListOfAIVendors,
  getPendingVendors,
  getRejectedVendors,
  getVerifiedVendors,
  respondToAIVerification,
} from 'features/vendors/vendorList.service'
import { type AIResult, type ListAIVendor, type ListVendor } from 'features/vendors/vendorList.types'

import { type VendorAIStatus } from '../vendor/vendor.types'

export interface VendorListState {
  verifiedVendorList: ListVendor[]
  verifiedCount: number
  verifiedPage: number
  getVerifiedVendorListStatus: StoreStatus
  verifiedSearch: string
  rejectedVendorList: ListVendor[]
  rejectedCount: number
  rejectedPage: number
  getRejectedVendorListStatus: StoreStatus
  rejectedSearch: string
  pendingVendorList: ListVendor[]
  pendingCount: number
  pendingPage: number
  getPendingVendorListStatus: StoreStatus
  pendingSearch: string
  aiVendorList: ListAIVendor[]
  aiVendorPage: number
  aiVendorStatus: StoreStatus
  aiVendorCount: number
  aiVendorSearch: string
  respondVerificationAIStatus: StoreStatus
  aiResults: AIResult[]
  aiResultsStatus: StoreStatus
}

const initialState: VendorListState = {
  verifiedVendorList: [],
  verifiedCount: 0,
  verifiedPage: 0,
  getVerifiedVendorListStatus: StoreStatus.Idle,
  verifiedSearch: '',
  rejectedVendorList: [],
  rejectedCount: 0,
  rejectedPage: 0,
  getRejectedVendorListStatus: StoreStatus.Idle,
  rejectedSearch: '',
  pendingVendorList: [],
  pendingCount: 0,
  pendingPage: 0,
  getPendingVendorListStatus: StoreStatus.Idle,
  pendingSearch: '',
  aiVendorList: [],
  aiVendorPage: 0,
  aiVendorStatus: StoreStatus.Idle,
  aiVendorCount: 0,
  aiVendorSearch: '',
  respondVerificationAIStatus: StoreStatus.Idle,
  aiResults: [],
  aiResultsStatus: StoreStatus.Idle,
}

export const VendorListSlice = createSlice({
  name: 'vendorList',
  initialState,
  reducers: {
    resetState: () => ({
      ...initialState,
    }),
    setVerifiedSearch: (state, { payload }: { payload: string }) => ({
      ...state,
      verifiedSearch: payload,
    }),
    setRejectedSearch: (state, { payload }: { payload: string }) => ({
      ...state,
      rejectedSearch: payload,
    }),
    setPendingVendorsSearch: (state, { payload }: { payload: string }) => ({
      ...state,
      pendingSearch: payload,
    }),
    setAIVendorSearch: (state, { payload }: { payload: string }) => ({
      ...state,
      aiVendorSearch: payload,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(getVerifiedVendors.pending, (state, { meta }) => {
      return {
        ...state,
        verifiedPage: meta.arg.page,
        getVerifiedVendorListStatus: StoreStatus.Loading,
      }
    })
    builder.addCase(getVerifiedVendors.fulfilled, (state, { payload }) => ({
      ...state,
      verifiedVendorList: payload.list.data,
      verifiedCount: payload.list.count,
      getVerifiedVendorListStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(getVerifiedVendors.rejected, (state) => ({
      ...state,
      verifiedVendorList: [],
      verifiedCount: 0,
      getVerifiedVendorListStatus: StoreStatus.Failed,
    }))
    builder.addCase(getRejectedVendors.pending, (state, { meta }) => ({
      ...state,
      rejectedPage: meta.arg.page,
      getRejectedVendorListStatus: StoreStatus.Loading,
    }))
    builder.addCase(getRejectedVendors.fulfilled, (state, { payload }) => ({
      ...state,
      rejectedVendorList: payload.list.data,
      rejectedCount: payload.list.count,
      getRejectedVendorListStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(getRejectedVendors.rejected, (state) => ({
      ...state,
      rejectedVendorList: [],
      rejectedCount: 0,
      getRejectedVendorListStatus: StoreStatus.Failed,
    }))
    builder.addCase(getPendingVendors.pending, (state, { meta }) => {
      return {
        ...state,
        pendingPage: meta.arg.page,
        getPendingVendorListStatus: StoreStatus.Loading,
      }
    })
    builder.addCase(getPendingVendors.fulfilled, (state, { payload }) => ({
      ...state,
      pendingVendorList: payload.list.data,
      pendingCount: payload.list.count,
      getPendingVendorListStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(getPendingVendors.rejected, (state) => ({
      ...state,
      pendingVendorList: [],
      pendingCount: 0,
      getPendingVendorListStatus: StoreStatus.Failed,
    }))
    builder.addCase(getListOfAIVendors.pending, (state, { meta }) => {
      return {
        ...state,
        aiVendorPage: meta.arg.page,
        aiVendorStatus: StoreStatus.Loading,
      }
    })
    builder.addCase(getListOfAIVendors.fulfilled, (state, { payload }) => ({
      ...state,
      aiVendorList: payload.list.data,
      aiVendorCount: payload.list.count,
      aiVendorStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(getListOfAIVendors.rejected, (state) => ({
      ...state,
      aiVendorList: [],
      aiVendorCount: 0,
      aiVendorStatus: StoreStatus.Failed,
    }))
    builder.addCase(respondToAIVerification.pending, (state) => ({
      ...state,
      respondVerificationStatus: StoreStatus.Loading,
    }))
    builder.addCase(respondToAIVerification.fulfilled, (state, { payload, meta }) => {
      const { vendorId, aiStatus } = payload
      const note = meta.arg.aiVerificationNote

      const updatedAiVendorList = state.aiVendorList?.map((vendor) =>
        vendor.id === vendorId
          ? {
              ...vendor,
              aiStatus: aiStatus as VendorAIStatus,
              aiVerificationNote: note ?? '',
            }
          : vendor,
      )

      state.respondVerificationAIStatus = StoreStatus.Succeeded
      state.aiVendorList = updatedAiVendorList
    })
    builder.addCase(respondToAIVerification.rejected, (state) => ({
      ...state,
      respondVerificationStatus: StoreStatus.Failed,
    }))
    builder.addCase(getAIResults.pending, (state, { payload }) => ({
      ...state,
      aiResultsStatus: StoreStatus.Loading,
    }))
    builder.addCase(getAIResults.fulfilled, (state, { payload }) => ({
      ...state,
      aiResults: payload,
      aiResultsStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(getAIResults.rejected, (state) => ({
      ...state,
      aiResults: [],
      aiResultsStatus: StoreStatus.Failed,
    }))
  },
})

export const {
  resetState,
  setVerifiedSearch,
  setRejectedSearch,
  setPendingVendorsSearch,
  setAIVendorSearch,
} = VendorListSlice.actions

export default VendorListSlice.reducer
